import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Upload,
  Description,
  Person,
  Business,
  Security,
  Help,
  Info,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';

const VerificationWizard = ({ verificationProgress, userType, onUploadDocument, onSubmitVerification }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  useEffect(() => {
    if (verificationProgress) {
      const steps = verificationProgress.steps || {};
      const completed = new Set();
      
      console.log('VerificationWizard - verificationProgress:', verificationProgress);
      console.log('VerificationWizard - steps:', steps);
      
      if (userType === 'pharmacy') {
        if (steps.profileComplete) completed.add(0);
        if (steps.licenseUploaded) completed.add(1);
        if (steps.businessRegistrationUploaded) completed.add(2);
        if (steps.taxCertificateUploaded) completed.add(3);
      } else if (userType === 'rider') {
        if (steps.profileComplete) completed.add(0);
        if (steps.idUploaded) completed.add(1);
        if (steps.licenseUploaded) completed.add(2);
        if (steps.vehicleRegistrationUploaded) completed.add(3);
        if (steps.insuranceUploaded) completed.add(4);
      }
      
      console.log('VerificationWizard - completed steps:', Array.from(completed));
      setCompletedSteps(completed);
      
      // Set active step to first incomplete step
      const firstIncomplete = Array.from(completed).length;
      setActiveStep(firstIncomplete);
    }
  }, [verificationProgress, userType]);

  const getSteps = () => {
    if (userType === 'pharmacy') {
      return [
        {
          label: 'Complete Profile',
          description: 'Fill in your pharmacy information',
          icon: <Business />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Make sure you have completed all required fields in your pharmacy profile:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Pharmacy Name" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="License Number" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Complete Address" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Contact Information" />
                </ListItem>
              </List>
              {completedSteps.has(0) ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  ✅ Profile completed successfully!
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Go to your profile settings to complete this step.
                  </Typography>
                </Alert>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Pharmacy License',
          description: 'Upload your official pharmacy license',
          icon: <Description />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload a clear photo or scan of your pharmacy operating license.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📋 Requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Clear, readable document" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="JPEG, PNG, or PDF format" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Maximum file size: 5MB" />
                  </ListItem>
                </List>
              </Paper>
              {completedSteps.has(1) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Pharmacy license uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('license')}
                  sx={{ mt: 1 }}
                >
                  Upload Pharmacy License
                </Button>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Business Registration',
          description: 'Upload your business registration document',
          icon: <Business />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your official business registration certificate.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  💡 Tip:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This document proves your business is legally registered and authorized to operate.
                </Typography>
              </Paper>
              {completedSteps.has(2) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Business registration uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('businessRegistration')}
                  sx={{ mt: 1 }}
                >
                  Upload Business Registration
                </Button>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Tax Certificate',
          description: 'Upload your tax registration certificate',
          icon: <Security />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your tax registration certificate or TIN document.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ⚠️ Important:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This document is required for tax compliance and business operations.
                </Typography>
              </Paper>
              {completedSteps.has(3) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Tax certificate uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('taxCertificate')}
                  sx={{ mt: 1 }}
                >
                  Upload Tax Certificate
                </Button>
              )}
            </Box>
          )
        }
      ];
    } else if (userType === 'rider') {
      return [
        {
          label: 'Complete Profile',
          description: 'Fill in your rider information',
          icon: <Person />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Make sure you have completed all required fields in your rider profile:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Full Name" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Phone Number" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Vehicle Information" />
                </ListItem>
              </List>
              {completedSteps.has(0) ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  ✅ Profile completed successfully!
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Go to your profile settings to complete this step.
                  </Typography>
                </Alert>
              )}
            </Box>
          )
        },
        {
          label: 'Upload National ID',
          description: 'Upload your government-issued ID',
          icon: <Person />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload a clear photo of your national ID card or passport.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📋 Requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Government-issued ID" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Clear photo of both sides" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Valid and not expired" />
                  </ListItem>
                </List>
              </Paper>
              {completedSteps.has(1) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ National ID uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('nationalId')}
                  sx={{ mt: 1 }}
                >
                  Upload National ID
                </Button>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Driver License',
          description: 'Upload your valid driver license',
          icon: <Description />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your valid driver's license for vehicle operation.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  💡 Tip:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Make sure your license is valid and shows the correct vehicle class.
                </Typography>
              </Paper>
              {completedSteps.has(2) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Driver license uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('driverLicense')}
                  sx={{ mt: 1 }}
                >
                  Upload Driver License
                </Button>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Vehicle Registration',
          description: 'Upload your vehicle registration document',
          icon: <Business />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your vehicle registration certificate.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  📋 Requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Current registration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Matches your vehicle info" />
                  </ListItem>
                </List>
              </Paper>
              {completedSteps.has(3) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Vehicle registration uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('vehicleRegistration')}
                  sx={{ mt: 1 }}
                >
                  Upload Vehicle Registration
                </Button>
              )}
            </Box>
          )
        },
        {
          label: 'Upload Insurance Certificate',
          description: 'Upload your vehicle insurance document',
          icon: <Security />,
          content: (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Upload your vehicle insurance certificate.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ⚠️ Important:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Insurance must be valid and cover commercial delivery operations.
                </Typography>
              </Paper>
              {completedSteps.has(4) ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Insurance certificate uploaded successfully!
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body2" color="success.contrastText">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      This document has been uploaded and is ready for review.
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => onUploadDocument('insurance')}
                  sx={{ mt: 1 }}
                >
                  Upload Insurance Certificate
                </Button>
              )}
            </Box>
          )
        }
      ];
    }
    return [];
  };

  const steps = getSteps();
  const allStepsCompleted = completedSteps.size === steps.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step) => {
    setActiveStep(step);
  };

  if (!verificationProgress) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Verification Status
          </Typography>
          <Alert severity="info">
            Loading verification information...
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          <Security sx={{ 
            fontSize: { xs: 24, sm: 32 }, 
            color: 'primary.main', 
            mr: { xs: 0, sm: 2 },
            mb: { xs: 1, sm: 0 }
          }} />
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Account Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Complete these steps to verify your {userType} account
            </Typography>
          </Box>
        </Box>

        {/* Progress Overview */}
        <Paper sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: { xs: 2, sm: 3 }, 
          bgcolor: 'primary.light', 
          color: 'primary.contrastText' 
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {verificationProgress.progress}% Complete
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {verificationProgress.completedSteps} of {verificationProgress.totalSteps} steps completed
              </Typography>
            </Box>
            {allStepsCompleted && (
              <Chip
                label="Ready to Submit"
                color="success"
                icon={<CheckCircle />}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  height: { xs: 28, sm: 32 }
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Step-by-step Wizard */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label} completed={completedSteps.has(index)}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{ cursor: 'pointer' }}
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: { xs: 32, sm: 40 },
                      height: { xs: 32, sm: 40 },
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                      color: completed || active ? 'white' : 'grey.600',
                      cursor: 'pointer'
                    }}
                  >
                    {completed ? <CheckCircle sx={{ fontSize: { xs: 18, sm: 24 } }} /> : 
                     React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                  </Box>
                )}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {step.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {step.description}
                  </Typography>
                </Box>
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                  {step.content}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    size="small"
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: { xs: 'auto', sm: 'auto' }
                    }}
                  >
                    Back
                  </Button>
                  {activeStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                      size="small"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        minWidth: { xs: 'auto', sm: 'auto' }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* Submit Section */}
        {allStepsCompleted && verificationProgress?.status === 'pending' && (
          <Box sx={{ 
            mt: { xs: 2, sm: 4 }, 
            p: { xs: 2, sm: 3 }, 
            bgcolor: 'success.light', 
            borderRadius: 2 
          }}>
            <Typography variant="h6" gutterBottom color="success.contrastText" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              🎉 Congratulations! All steps completed!
            </Typography>
            <Typography variant="body2" color="success.contrastText" sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              You've completed all required verification steps. Submit your application for review.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => {
                console.log('Submit for verification button clicked in VerificationWizard');
                if (onSubmitVerification) {
                  onSubmitVerification();
                } else {
                  console.error('onSubmitVerification handler is not provided');
                }
              }}
              sx={{ 
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Submit for Verification
            </Button>
          </Box>
        )}

        {allStepsCompleted && verificationProgress?.status === 'in_progress' && (
          <Box sx={{ 
            mt: { xs: 2, sm: 4 }, 
            p: { xs: 2, sm: 3 }, 
            bgcolor: 'info.light', 
            borderRadius: 2 
          }}>
            <Typography variant="h6" gutterBottom color="info.contrastText" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              📋 Verification Submitted!
            </Typography>
            <Typography variant="body2" color="info.contrastText" sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              Your verification application has been submitted successfully and is now under review.
            </Typography>
            <Typography variant="body2" color="info.contrastText" sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              You will be notified once the review is complete. This process typically takes 1-3 business days.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <Typography variant="body2" color="info.contrastText" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Status: Under Review
              </Typography>
            </Box>
          </Box>
        )}

        {allStepsCompleted && verificationProgress?.status === 'verified' && (
          <Box sx={{ 
            mt: { xs: 2, sm: 4 }, 
            p: { xs: 2, sm: 3 }, 
            bgcolor: 'success.light', 
            borderRadius: 2 
          }}>
            <Typography variant="h6" gutterBottom color="success.contrastText" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              ✅ Verification Complete!
            </Typography>
            <Typography variant="body2" color="success.contrastText" sx={{ 
              mb: 2,
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              Congratulations! Your verification has been approved. You can now access all platform features.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle color="inherit" sx={{ fontSize: { xs: 16, sm: 20 } }} />
              <Typography variant="body2" color="success.contrastText" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Status: Verified
              </Typography>
            </Box>
          </Box>
        )}

        {/* Help Section */}
        <Box sx={{ 
          mt: { xs: 2, sm: 3 }, 
          p: { xs: 1.5, sm: 2 }, 
          bgcolor: 'grey.50', 
          borderRadius: 1 
        }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            <Help sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, verticalAlign: 'middle' }} />
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            If you're having trouble with any step, contact our support team. 
            We're here to help you complete your verification quickly and easily.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VerificationWizard;
